import styled from '@emotion/styled';
import { useLingui } from '@lingui/react/macro';
import classNames from 'classnames';
import { useAtomValue } from 'jotai';

import { usePreferences } from '@/react-query/usePreferences';
import { useImageFallback } from '@/utils/hooks';
import { getPreferredLanguageKey } from '@/utils/internationalization/locale';
import { mergeLocalizedCustomCopy } from '@/utils/treatmentOptions';

import { VideaTypography } from '../../../../common-components/elements/VideaTypography/VideaTypography';
import { fontFamiliesNeueHaasGroteskDisplayPro } from '../../../../theme/videa-style-tokens';
import { previewDisplayDeviceAtom } from '../../multiFileViewer/imageToolbar/atoms';
import type {
  AdultTreatmentKey,
  AdultTreatmentOption,
} from '../../patientEducationDialog/AdultTabs';
import {
  type PediatricTreatmentKey,
  pediatricTreatmentKeys,
  usePediatricTreatmentOptions,
} from '../../patientEducationDialog/PediatricTabs';

export const useAdultTreatmentOptions = (
  options: { getDefault?: boolean; forPrintout?: boolean } = {},
): Partial<Record<AdultTreatmentKey, AdultTreatmentOption>> => {
  const { getDefault = false, forPrintout = false } = options;
  const {
    t,
    i18n: { locale },
  } = useLingui();
  const { data: preferences } = usePreferences();
  const customCopy = preferences?.customPatientEducationTxCopy;
  const isPerioCarriersEnabled = !!preferences?.perioCarriersEnabled;
  const lang = getPreferredLanguageKey(locale);

  const isToothEnamelRepairCodeEnabled =
    !!preferences?.toothEnamelRepairCodeEnabled;
  let toothEnamelRepairLabel = t`Tooth Enamel Repair`;
  if (isToothEnamelRepairCodeEnabled) {
    toothEnamelRepairLabel = t`Tooth Enamel Repair (D2991)`;
  }

  const defaultTreatments: Partial<
    Record<AdultTreatmentKey, AdultTreatmentOption>
  > = {
    filling: {
      label: t`Filling`,
      description: t`A dental filling is a restorative material used to repair a tooth that has been damaged by decay or minor fractures. The dentist removes the decayed or damaged portion of the tooth, cleans the affected area, and then fills the cavity with a material such as white composite resin or silver amalgam. Fillings help restore the tooth's function and shape while preventing further decay or breaks.`,
      src: `assets/images/patientEducation/filling-${locale}.png`,
      fallback: 'assets/images/patientEducation/filling-en.png',
      value: 'filling',
      isPediatric: false,
    },
    rct: {
      label: t`Root Canal`,
      description: t`A root canal ("endodontic therapy") is a dental procedure used to treat infection or inflammation in the pulp of a tooth. During the procedure, the dentist removes the damaged pulp, cleans and shapes the root canal system, then fills and seals it with a plastic material. This treatment saves a tooth that would otherwise need to be extracted, allowing patients to maintain their natural smile and normal biting function. Teeth that have had root canals almost always require a crown to protect the tooth from further damage.`,
      src: `assets/images/patientEducation/rootCanal-${locale}.png`,
      fallback: 'assets/images/patientEducation/rootCanal-en.png',
      value: 'rct',
      isPediatric: false,
    },
    srp: {
      label: t`SRP`,
      description: t`Scaling and root planing (SRP) is a deep cleaning procedure used to treat periodontal (gum) disease. It involves removing plaque and calculus, also known as tartar, from both above and below the gum line. This non-surgical treatment helps prevent the progression of gum disease and promotes healing of the gum tissue. Depending on the severity, it may take multiple appointments to complete SRP in the entire mouth.`,
      src: `assets/images/patientEducation/perio-${locale}.png`,
      fallback: 'assets/images/patientEducation/perio-en.png',
      value: 'srp',
      isPediatric: false,
    },
    crown: {
      label: t`Crown`,
      description: t`A dental crown is a tooth-shaped cap that is placed over a damaged or weakened tooth to restore its shape, size, strength, and appearance. To ensure a proper fit, a dentist will need to remove a small amount of tooth structure before cementing your new crown in place. Crowns are commonly used to protect a weak tooth from breaking, restore a broken tooth, or cover a tooth with a large filling or one that has undergone a root canal procedure.`,
      src: `assets/images/patientEducation/crown-${locale}.png`,
      fallback: 'assets/images/patientEducation/crown-en.png',
      value: 'crown',
      isPediatric: false,
    },
    tooth_remineralization: {
      label: toothEnamelRepairLabel,
      description: t`Incipient dental decay, or early-stage tooth decay, can often be reversed through the application of a non-invasive treatment for early stage tooth decay. This product helps restore the weakened tooth enamel, effectively halting and potentially reversing the decay process. This treatment can prevent the need for more invasive procedures and help maintain and restore the natural structure of the tooth.`,
      src: `assets/images/patientEducation/filling-${locale}.png`,
      fallback: 'assets/images/patientEducation/filling-en.png',
      value: 'tooth_remineralization',
      isPediatric: false,
    },
    implant: {
      label: t`Implant`,
      description: t`A dental implant is an artificial tooth root surgically placed into the jawbone to replace a missing tooth. Implants are titanium posts that fuse with the bone. Once healed and topped with a custom-made crown, a dental implant looks, feels, and functions like a natural tooth, restoring both aesthetics and chewing ability.`,
      src: `assets/images/patientEducation/implant-${locale}.png`,
      fallback: 'assets/images/patientEducation/implant-en.png',
      value: 'implant',
      isPediatric: false,
    },
    aligner: {
      label: t`Aligner`,
      description: t`Clear aligners are a modern, nearly invisible way to straighten teeth without metal braces. They are custom-made, removable trays that gently move teeth into the right position over time. Because they can be taken out for eating and brushing, it's easier to keep teeth and gums clean and healthy. Clear aligners offer a comfortable way to correct crowding or spacing and maintain a confident smile.`,
      src: 'assets/images/patientEducation/aligner-en.png',
      fallback: 'assets/images/patientEducation/aligner-en.png',
      value: 'aligner',
      isPediatric: false,
    },
    bridge: {
      label: t`Bridge`,
      description: t`A dental bridge is a solution for replacing one or more missing teeth. It fills the gap where teeth are missing by attaching to the teeth or implants on either side. The bridge is made as one solid piece that includes artificial teeth designed to look and function like natural teeth. It is cemented securely in place by your dentist and cannot be removed at home. A dental bridge helps you chew and speak more easily, keeps nearby teeth from shifting out of position, and supports your overall oral health.`,
      src: `assets/images/patientEducation/bridge-en.mp4`,
      fallback: 'assets/images/patientEducation/bridge-en.mp4',
      value: 'bridge',
      isPediatric: false,
      isVideo: true,
    },
    perio_carriers: {
      label: t`Perio Carriers (D5995/D5996)`,
      description: t`Periodontal (gum) disease is a bacterial infection that can be difficult to control between dental visits. Periodontal medicament carriers are trays designed to deliver medication below the gumline, where bacteria cause inflammation and infection. By wearing these trays for just 10–15 minutes a day, the medication reaches areas that brushing and flossing can’t, helping reduce harmful bacteria, promote healthier gums, and freshen your breath.`,
      src: `assets/images/patientEducation/perio-carriers-${locale}.png`,
      fallback: 'assets/images/patientEducation/perio-carriers-en.png',
      value: 'perio_carriers',
      isPediatric: false,
    },
    periodontal_maintenance: {
      label: t`Perio Maintenance`,
      description: t`Periodontal maintenance is an ongoing, lifelong treatment that follows scaling and root planing to help control gum disease and preserve the health of your gums and bone. Unlike a standard dental cleaning, it involves deeper cleaning beneath the gumline and regular monitoring of pocket depths to prevent harmful bacteria from returning. It's typically done every 3–4 months to disrupt bacterial growth, reduce inflammation, and protect against future bone loss. Staying on track with your dental visits, along with daily brushing, flossing, and any prescribed rinses, is essential for keeping your mouth healthy long-term and for your overall health, as periodontal disease has been linked to diseases such as diabetes and heart disease.`,
      src: `assets/images/patientEducation/perio-maintenance-${locale}.png`,
      fallback: 'assets/images/patientEducation/perio-maintenance-en.png',
      value: 'periodontal_maintenance',
      isPediatric: false,
    },
  };

  // Filter out perio_carriers if feature flag is not enabled
  const filteredTreatments = { ...defaultTreatments };
  if (!isPerioCarriersEnabled) {
    delete filteredTreatments.perio_carriers;
  }

  // For printouts, convert bridge from video to static image
  if (forPrintout) {
    filteredTreatments.bridge = {
      ...filteredTreatments.bridge!,
      src: `assets/images/patientEducation/bridge-en.png`,
      fallback: 'assets/images/patientEducation/bridge-en.png',
      isVideo: false,
    };
  }

  // If custom copy is available, merge it with default treatments
  if (customCopy && !getDefault) {
    return mergeLocalizedCustomCopy(filteredTreatments, customCopy, lang);
  }

  return filteredTreatments;
};

const StyledTreatmentDescription = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
  gap: 1.5em;

  &.videa-mobile-preview {
    flex-direction: column;

    img {
      order: -1;
      width: 100%;
    }
  }

  .treatment-title {
    font-size: 1em;
    font-family: ${fontFamiliesNeueHaasGroteskDisplayPro};
    font-weight: 700;
  }
  .treatment-description {
    font-size: 1em;
  }
`;

const isPediatricTreatment = (
  treatment: AdultTreatmentKey | PediatricTreatmentKey,
): treatment is PediatricTreatmentKey => {
  return pediatricTreatmentKeys.includes(treatment as PediatricTreatmentKey);
};

export const TreatmentsText = ({
  showDiagram,
  treatment,
  className,
}: {
  showDiagram: boolean;
  treatment: AdultTreatmentKey | PediatricTreatmentKey;
  className?: string;
}) => {
  const adultOptions = useAdultTreatmentOptions({ forPrintout: true });
  const pediatricOptions = usePediatricTreatmentOptions();
  const treatmentOption = isPediatricTreatment(treatment)
    ? pediatricOptions[treatment]
    : adultOptions[treatment];

  const { imgSrc, onError } = useImageFallback(
    treatmentOption?.src ?? '',
    treatmentOption?.fallback ?? '',
  );
  const previewDisplayDevice = useAtomValue(previewDisplayDeviceAtom);

  if (!treatmentOption) {
    return null;
  }

  return (
    <StyledTreatmentDescription
      className={classNames(
        `treatment-option ${
          previewDisplayDevice === 'mobile'
            ? 'videa-mobile-preview'
            : 'desktop-preview'
        }`,
        className,
      )}
    >
      <div
        className="vai-w-full vai-py-4"
        data-testid={`treatment-option-${treatment}`}
      >
        <VideaTypography variant="labelSmallStrong" className="treatment-title">
          {treatmentOption.label}
        </VideaTypography>
        <VideaTypography className="treatment-description">
          {treatmentOption.description}
        </VideaTypography>
      </div>
      {showDiagram && imgSrc && (
        // eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions
        <img
          alt={`Diagram of ${treatmentOption.label}`}
          src={imgSrc}
          onError={onError}
          width="40%"
        />
      )}
    </StyledTreatmentDescription>
  );
};
